import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ImageCropModal.css';
import { Close as CloseIcon, Check as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { getMediaDisplayUrl } from '../../utils/mediaUtils';

const ImageCropModal = ({ isOpen, image, onCropComplete, onClose, aspectRatio = null }) => {
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [dragState, setDragState] = useState({
    isDragging: false,
    isResizing: false,
    startPos: { x: 0, y: 0 },
  });
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0,
    displayedWidth: 0,
    displayedHeight: 0,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const imageRef = useRef(null);

  // Calculs mémorisés pour les coordonnées et positions
  const { scaleFactor, imageOffset, getRelativePosition } = useMemo(() => {
    const factor =
      imageSize.displayedWidth && imageSize.width
        ? Math.min(
            imageSize.displayedWidth / imageSize.width,
            imageSize.displayedHeight / imageSize.height
          )
        : 1;

    const offset =
      imageRef.current && imageSize.displayedWidth
        ? (() => {
            const containerRect = imageRef.current.parentElement.getBoundingClientRect();
            const imageRect = imageRef.current.getBoundingClientRect();
            return {
              x: imageRect.left - containerRect.left,
              y: imageRect.top - containerRect.top,
            };
          })()
        : { x: 0, y: 0 };

    const getPos = (clientX, clientY) => {
      if (!imageRef.current) return { x: 0, y: 0 };
      const containerRect = imageRef.current.parentElement.getBoundingClientRect();
      return {
        x: (clientX - containerRect.left - offset.x) / factor,
        y: (clientY - containerRect.top - offset.y) / factor,
      };
    };

    return { scaleFactor: factor, imageOffset: offset, getRelativePosition: getPos };
  }, [imageSize]);

  // Style de la zone de recadrage
  const cropOverlayStyle = useMemo(
    () => ({
      left: Math.round(cropArea.x * scaleFactor + imageOffset.x),
      top: Math.round(cropArea.y * scaleFactor + imageOffset.y),
      width: Math.round(cropArea.width * scaleFactor),
      height: Math.round(cropArea.height * scaleFactor),
      position: 'absolute',
      pointerEvents: 'auto',
    }),
    [cropArea, scaleFactor, imageOffset]
  );

  // Gestionnaire unifié pour les événements souris
  const handleMouseDown = useCallback(
    (e, action) => {
      e.preventDefault();
      const position = getRelativePosition(e.clientX, e.clientY);
      setDragState({
        isDragging: action === 'drag',
        isResizing: action === 'resize',
        startPos: position,
      });
    },
    [getRelativePosition]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragState.isDragging && !dragState.isResizing) return;

      const currentPos = getRelativePosition(e.clientX, e.clientY);

      setCropArea((prev) => {
        if (dragState.isDragging) {
          const deltaX = currentPos.x - dragState.startPos.x;
          const deltaY = currentPos.y - dragState.startPos.y;
          return {
            ...prev,
            x: Math.max(0, Math.min(imageSize.width - prev.width, prev.x + deltaX)),
            y: Math.max(0, Math.min(imageSize.height - prev.height, prev.y + deltaY)),
          };
        }
        if (dragState.isResizing) {
          const minSize = 50;

          if (aspectRatio) {
            const newWidth = Math.max(minSize, currentPos.x - prev.x);
            let newHeight = newWidth / aspectRatio;

            if (prev.y + newHeight > imageSize.height) {
              newHeight = imageSize.height - prev.y;
              const adjustedWidth = newHeight * aspectRatio;
              return {
                ...prev,
                width: Math.min(adjustedWidth, imageSize.width - prev.x),
                height: newHeight,
              };
            }

            return {
              ...prev,
              width: Math.min(newWidth, imageSize.width - prev.x),
              height: newHeight,
            };
          }

          const newWidth = Math.max(minSize, currentPos.x - prev.x);
          const newHeight = Math.max(minSize, currentPos.y - prev.y);
          return {
            ...prev,
            width: Math.min(newWidth, imageSize.width - prev.x),
            height: Math.min(newHeight, imageSize.height - prev.y),
          };
        }
        return prev;
      });

      if (dragState.isDragging) {
        setDragState((prev) => ({ ...prev, startPos: currentPos }));
      }
    },
    [dragState, getRelativePosition, imageSize, aspectRatio]
  );

  const handleMouseUp = useCallback(() => {
    setDragState({ isDragging: false, isResizing: false, startPos: { x: 0, y: 0 } });
  }, []);

  // Initialisation de l'image et de la zone de recadrage
  const initializeImageAndCrop = useCallback(() => {
    if (!imageRef.current) return;

    const { clientWidth, clientHeight, naturalWidth, naturalHeight } = imageRef.current;

    setImageSize({
      width: naturalWidth,
      height: naturalHeight,
      displayedWidth: clientWidth,
      displayedHeight: clientHeight,
    });

    // Zone de recadrage centrée
    const ratio = aspectRatio || 1;
    let cropWidth;
    let cropHeight;

    if (aspectRatio) {
      const maxWidth = naturalWidth * 0.9;
      const maxHeight = naturalHeight * 0.9;
      cropWidth = Math.min(maxWidth, maxHeight * ratio);
      cropHeight = cropWidth / ratio;

      if (cropHeight > maxHeight) {
        cropHeight = maxHeight;
        cropWidth = cropHeight * ratio;
      }
    } else {
      const initialSize = Math.min(naturalWidth, naturalHeight) * 0.4;
      cropWidth = initialSize;
      cropHeight = initialSize;
    }

    setCropArea({
      x: (naturalWidth - cropWidth) / 2,
      y: (naturalHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    });
  }, [aspectRatio]);

  // Création de l'image recadrée optimisée
  const createCroppedImage = useCallback(async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = Math.round(cropArea.width);
    canvas.height = Math.round(cropArea.height);

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        try {
          ctx.drawImage(
            img,
            Math.round(cropArea.x),
            Math.round(cropArea.y),
            Math.round(cropArea.width),
            Math.round(cropArea.height),
            0,
            0,
            canvas.width,
            canvas.height
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Impossible de créer le blob'));
              }
            },
            image.type || 'image/png',
            0.9
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error("Impossible de charger l'image"));

      const src = image.file ? URL.createObjectURL(image.file) : getMediaDisplayUrl(image);
      if (!src) {
        reject(new Error("Aucune source d'image disponible"));
        return;
      }
      if (!src.startsWith('blob:') && !src.startsWith('data:')) {
        img.crossOrigin = 'anonymous';
      }
      img.src = src;
    });
  }, [cropArea, image]);

  const handleCrop = useCallback(async () => {
    setIsProcessing(true);

    try {
      const croppedBlob = await createCroppedImage();

      const croppedImage = {
        file: croppedBlob,
        displayUrl: URL.createObjectURL(croppedBlob),
        name: image.name || image.fileName || 'image.png',
        type: image.type || 'image/png',
        size: croppedBlob.size,
        extension: image.extension,
        lastModified: Date.now(),
        isCropped: true,
      };

      onCropComplete(croppedImage);
    } catch (error) {
      console.error('Erreur lors du recadrage:', error);
      alert("Erreur lors du recadrage de l'image. Veuillez réessayer.");
    } finally {
      setIsProcessing(false);
    }
  }, [createCroppedImage, image, onCropComplete]);

  // Effects groupés et optimisés
  useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
      return;
    }

    // Bloquer le scroll et gérer Escape
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isProcessing) onClose();
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isProcessing, onClose]);

  useEffect(() => {
    if (isOpen && image && imageRef.current) {
      const img = imageRef.current;
      if (img.complete) {
        setTimeout(initializeImageAndCrop, 100);
      } else {
        img.onload = () => setTimeout(initializeImageAndCrop, 100);
      }
    }
  }, [isOpen, image, initializeImageAndCrop]);

  useEffect(() => {
    if (!dragState.isDragging && !dragState.isResizing) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.isDragging, dragState.isResizing, handleMouseMove, handleMouseUp]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="image-crop-modal-overlay"
      onClick={(e) => !isProcessing && e.target === e.currentTarget && onClose()}
    >
      <div className="image-crop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Recadrer l'image</h3>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            disabled={isProcessing}
            aria-label="Fermer"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          <div className="image-crop-container">
            <img
              ref={imageRef}
              src={getMediaDisplayUrl(image)}
              alt="Image à recadrer"
              className="crop-image"
              draggable={false}
            />

            {imageSize.width > 0 && scaleFactor > 0 && (
              <div
                className="crop-overlay"
                style={cropOverlayStyle}
                onMouseDown={(e) => !isProcessing && handleMouseDown(e, 'drag')}
              >
                <div className="crop-border"></div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => {
                    if (isProcessing) return;
                    e.stopPropagation();
                    handleMouseDown(e, 'resize');
                  }}
                />
              </div>
            )}
          </div>

          {isProcessing && (
            <div className="crop-processing-overlay" aria-live="polite" aria-busy="true">
              <div className="crop-processing-spinner" />
              <p>Traitement en cours...</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="cancel-button" onClick={onClose} disabled={isProcessing}>
            <CancelIcon fontSize="small" />
            Annuler
          </button>
          <button
            type="button"
            className="confirm-button"
            onClick={handleCrop}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="crop-processing-spinner crop-processing-spinner--inline" />
                Traitement...
              </>
            ) : (
              <>
                <CheckIcon fontSize="small" />
                Recadrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageCropModal;
